import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Info, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { ProjectThumb } from "@/components/site/ProjectCard";
import { usePageMeta } from "@/lib/usePageMeta";

export default function ProjectPage() {
  const { slug } = useParams();
  const { projects } = useSite();
  const [p, setP] = useState(undefined);

  useEffect(() => {
    setP(undefined);
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [slug]);
  usePageMeta(p?.title, p?.short_description);

  if (p === undefined) return <div className="min-h-[70vh] pt-32 container-lm" data-testid="project-loading"><div className="h-10 w-64 bg-[#EEE7DC] rounded animate-pulse" /></div>;
  if (p === null)
    return (
      <div className="min-h-[70vh] pt-36 container-lm" data-testid="project-not-found">
        <h1 className="font-display text-3xl font-semibold">Layihə tapılmadı</h1>
        <Link to="/layiheler" className="btn-dark mt-6">Bütün işlər</Link>
      </div>
    );

  const others = projects.filter((x) => x.slug !== p.slug).slice(0, 4);
  return (
    <article data-testid="project-page" className="pt-28 pb-20">
      <div className="container-lm">
        <Link to="/layiheler" className="link-arrow text-sm" data-testid="project-back-link"><ArrowLeft size={15} /> Bütün işlər</Link>
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 mt-8 items-start">
          <div>
            <p className="eyebrow">{p.category_label}</p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-4" data-testid="project-title">{p.title}</h1>
            <p className="text-[#77736B] mt-5 leading-relaxed whitespace-pre-line">{p.description || p.short_description}</p>
            {p.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">{p.tags.map((t) => <span key={t} className="text-xs bg-[#EEE7DC] px-3 py-1.5 rounded-full">{t}</span>)}</div>
            )}
            {p.is_concept && (
              <p className="mt-8 flex gap-3 text-sm bg-white/70 border border-[#D6AD62]/40 rounded-xl p-4" data-testid="project-concept-note">
                <Info size={18} className="text-[#b58a3f] shrink-0" /> Bu, konsept / nümunə layihədir və real müştəri sifarişi kimi təqdim edilmir.
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/#contact" className="btn-dark" data-testid="project-contact-btn">Oxşar layihə istəyirəm <ArrowRight size={16} className="arr" /></Link>
              {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="project-external-link">Bax <ExternalLink size={14} /></a>}
            </div>
          </div>
          <div className={`rounded-2xl overflow-hidden ${p.category === "logo" ? "bg-white p-10 flex items-center justify-center" : "bg-[#EEE7DC]"}`}>
            <img src={p.image} alt={p.title} className={p.category === "logo" ? "max-w-full max-h-[420px] object-contain" : "w-full h-auto"} />
          </div>
        </div>
        {p.gallery?.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mt-10">{p.gallery.map((g) => <img key={g} src={g} alt={p.title} loading="lazy" className={`rounded-xl w-full ${p.category === "logo" ? "bg-white p-8 aspect-[4/3] object-contain" : ""}`} />)}</div>
        )}
        {others.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display text-2xl font-semibold">Digər işlər</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">{others.map((o) => <ProjectThumb key={o.id} p={o} />)}</div>
          </div>
        )}
      </div>
    </article>
  );
}
